// Prospecting (202610061200) — the Clay lead pipeline, inside Atlas.
//
// Left: batches. Main: the funnel for one batch — Intake → Identified →
// Qualified → Enriched → Sent, with Rejected aside — and a table of
// prospects: who they are, in Atlas or not, ownership, the industry the rules
// put them in (with confidence and the clause that decided it), geography
// fit, size tier with reasons, the owners found and their emails. A person
// approves; Send creates the company and contacts in Atlas and queues the
// contacts for the dialer. Nothing is sent by itself.
//
// Lead discovery (202610071700): a batch starts from a search — Apollo
// (organization search), Google Maps (Outscraper) or the Atlas lead pool —
// or from a pasted list / file. Rows can be approved in bulk; owners can be
// looked up per row (Apollo People Search when the site names nobody).
//
// How it got here (2026-10-08): a row opens a drawer with every verdict's
// reasoning and sources and the steps behind them, like the research
// wand's card. The run has no spend ceiling to set — admins watch the
// spend in Settings → Costs.

const STAGE_LABEL = { intake: 'Not read', identified: 'Identified', qualified: 'Qualified', enriched: 'Enriched', sent: 'Sent', rejected: 'Rejected' };
const TIER_HUE = { premium: 'green', strong: 'teal', qualified: 'blue', does_not_qualify: 'rose', unable: 'slate' };
const TIER_LABEL = { premium: 'Premium', strong: 'Strong', qualified: 'Qualified', does_not_qualify: 'Does not qualify', unable: 'Unable to tell' };
const OWN_HUE = { privately_held: 'green', retarget: 'amber', dq: 'rose', unsure: 'slate' };
const OWN_LABEL = { privately_held: 'Privately held', retarget: 'Retarget', dq: 'DQ', unsure: 'Unsure' };
const REASON_LABEL = { public: 'Public', funding: 'Funding >$200K', franchised: 'Franchised', nonprofit: 'Nonprofit', government: 'Government',
  acquired: 'Acquired', subsidiary: 'Subsidiary', pe_backed: 'PE backed', closed: 'Closed' };
const PARENT_LABEL = { public: 'publicly traded', pe_backed: 'PE-backed', closed: 'closed', private: 'private and operating', unknown: 'not confirmed' };
const CONF_HUE = { high: 'green', medium: 'amber', low: 'slate' };
// An address a finder built (the domain's pattern, the web finder) rather
// than saw confirmed: shown with its basis, never counted as an email, and
// kept off the contact when the row is sent (app/email_pattern).
const GUESSED = new Set(['guess', 'guessed', 'risky', 'invalid']);
const isGuess = (c) => !!c.email && GUESSED.has(c.email_status);
const hasEmail = (c) => !!c.email && !GUESSED.has(c.email_status);
// What Send did (app/prospecting.send → outreach): '1 to email · 1 to call', 'Nobody to reach'.
const sentCounts = (o) => {
  if (!o) return { text: 'Sent', none: false };
  if (!o.people) return { text: o.queued ? `${o.queued} to call` : (o.contact_ids || []).length ? 'Sent' : 'Nobody to reach', none: !o.queued && !(o.contact_ids || []).length };
  const n = (k) => o.people.filter((x) => x.email === k).length;
  const bits = [n('queued') ? `${n('queued')} to email` : null, n('waiting') ? `${n('waiting')} waiting for a campaign` : null, o.queued ? `${o.queued} to call` : null].filter(Boolean);
  return { text: bits.length ? bits.join(' · ') : 'Nobody to reach', none: !bits.length };
};
// What intake set aside or left out of a list (app/prospecting.LEFT_OUT_LABEL), zeros skipped.
const INTAKE_LEFT_OUT = [['already_prospected', 'already in another batch'], ['duplicates', 'already in this batch'], ['repeated', 'repeated in the list'],
  ['no_site', 'social or directory links with no name'], ['unreadable', 'with no website or name']];
const intakeBody = (it, extra) => [`${it.in_atlas || 0} already in Atlas`, `${it.flagged_live_deal || 0} with a live deal set aside`,
  ...INTAKE_LEFT_OUT.filter(([k]) => it[k] > 0).map(([k, label]) => `${it[k].toLocaleString()} ${label}`), ...(extra ? [extra] : [])].join(' · ');
// apiFetch throws "422 Unprocessable Entity: {"detail": "…"}" — the server's sentence.
const errDetail = (e) => {
  const msg = (e && e.message) || '';
  const m = /^\d{3}\b[^:]*:\s*([\s\S]*)$/.exec(msg);
  if (!m) return msg || 'Something went wrong';
  try { const j = JSON.parse(m[1]); if (j && typeof j.detail === 'string') return j.detail; } catch (_) { /* not JSON */ }
  return m[1].trim() || msg;
};
const sentToast = (r) => {
  const c = sentCounts(r && r.outreach);
  window.pushToast?.({ type: c.none ? 'warning' : 'success', title: c.none ? 'Sent — nobody to reach' : `Sent — ${c.text}` });
};
const EMAIL_OUTCOME = (x, camp) => x.email === 'queued'
  ? { hue: 'green', label: `Email · ${camp ? camp.name : 'Email Copilot'}${camp && camp.mode === 'shadow' ? ' (shadow)' : ''}${camp && camp.paused ? ' · paused' : ''}` }
  : x.email === 'waiting' ? { hue: 'amber', label: 'Email · waiting for a campaign' } : { hue: 'slate', label: 'Not emailed' };
// Calls come after the emails (app/prospecting.call_plan): PhoneBurner gets the
// emailed owner after the campaign's last email, if they haven't replied; the call queue takes, at once, only
// people the Email Copilot can't email.
const CALL_OUTCOME = {
  after_email: { hue: 'blue', label: 'Call · after the emails' },
  queued: { hue: 'green', label: 'Call queue' },
  colleague: { hue: 'slate', label: 'Not called · a colleague is emailed' },
  skipped: { hue: 'slate', label: 'Not called' },
  no_phone: { hue: 'slate', label: 'No phone' },
};
const GEO_HUE = { yes: 'green', no: 'rose', uncertain: 'slate' };
const GEO_LABEL = { yes: 'In area', no: 'Out of area', uncertain: 'Unclear' };
const STEP_LABEL = { identify: 'Read the site', ownership: 'Ownership check', qualify: 'Qualify (rules)', revenue: 'Estimate revenue', contacts: 'Find owners (Apollo)' };
const SOURCE_LABEL = { apollo: 'Apollo', maps: 'Google Maps', atlas: 'Atlas lead pool', paste: 'Pasted', csv: 'File', signals: 'Signals', manual: 'Manual' };
const SOURCE_SHORT = { apollo: 'Apollo', maps: 'Maps', atlas: 'Atlas', signals: 'Signals' };
const SKIP_SHORT = { nonprofit: 'nonprofits', government: 'government', media: 'media', software: 'software', funded: 'funded', subsidiary: 'subsidiaries' };
const COUNTRY_SHORT = { US: 'US', CA: 'Canada', GB: 'UK', AU: 'Australia' };
const FOUND_LABEL = { apollo: 'Found by an Apollo company search', maps: 'Found on Google Maps', atlas: 'Picked from the Atlas lead pool',
  paste: 'Pasted into the batch', csv: 'From an uploaded file', signals: 'From the Signals page', manual: 'Added by hand' };
const GRID = '26px minmax(180px, 1.5fr) 60px 134px minmax(150px, 1.3fr) 80px 112px 92px 92px 132px';

const pct = (x) => (x == null ? '' : `${Math.round(Number(x) * 100)}%`);
const rangeLabel = (r) => String(r).replace(',', '–');
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);
// The revenue estimate's figures, shared with the company Revenue wand (research.jsx):
// one number when the estimate is high confidence, the range otherwise.
const revLabel = (est) => window.RevenueEstimate.label(est);
const ownLabel = (own) => `${OWN_LABEL[own.outcome] || own.outcome}${own.reason ? ` · ${REASON_LABEL[own.reason] || own.reason}` : ''}`;
// An Atlas company with the same name the site read did not confirm (another state). A merely
// similar name ("Behavioral Health" for every behavioral health lead) is not worth a line.
const sameName = (p) => !p.in_atlas && !!(p.atlas_state || {}).candidate && p.atlas_match_tier === 'name_exact';
const readFailures = (p) => (p.errors || []).filter((e) => /could not be read/.test((e && e.error) || '')).length;
// Apollo's headcount buckets, in order, contiguous ones merged: 1–10 … 101–200 → "1–200".
const rangesLabel = (rs) => {
  const xs = (rs || []).map((r) => String(r).split(',').map(Number)).filter((x) => x.length === 2 && x.every(Number.isFinite)).sort((a, b) => a[0] - b[0]);
  const out = [];
  xs.forEach(([lo, hi]) => { const last = out[out.length - 1]; if (last && lo <= last[1] + 1) last[1] = Math.max(last[1], hi); else out.push([lo, hi]); });
  return out.map(([lo, hi]) => `${lo}–${hi}`).join(', ');
};
const hostOf = (u) => {
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch (_) { return u || ''; }
};
const hostPath = (u) => {
  try { const x = new URL(u); return `${x.hostname.replace(/^www\./, '')}${x.pathname.replace(/\/$/, '')}`; } catch (_) { return u || ''; }
};
if (typeof document !== 'undefined' && !document.getElementById('atlas-prospect-rows')) {
  const st = document.createElement('style');
  st.id = 'atlas-prospect-rows';
  st.textContent = '.prospect-row:hover { background: var(--surface-hover); } .prospect-row[data-sel="1"] { background: var(--accent-soft); }'
    + ' .prospect-row:hover .why-btn { color: var(--accent-text) !important; } .prospect-row select:hover { border-color: var(--border) !important; }';
  document.head.appendChild(st);
}
const chipStyle = (on, disabled) => ({ fontSize: 11.5, padding: '3px 9px', borderRadius: 999, border: '1px solid', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
  borderColor: on ? 'var(--text)' : 'var(--border)', background: on ? 'var(--text)' : 'transparent', color: on ? 'var(--surface)' : 'var(--text-3)' });
const selectStyle = { fontSize: 12, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' };

// Free text → chips. Enter, comma or blur adds; backspace on an empty box
// removes the last. `listId` wires a <datalist> (window.GEO.LIST_IDS.state).
const ChipsInput = ({ values, onChange, placeholder, listId, disabled }) => {
  const [text, setText] = React.useState('');
  const add = (raw) => {
    const parts = String(raw || '').split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...values];
    parts.forEach((p) => { if (!next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p); });
    onChange(next); setText('');
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', opacity: disabled ? 0.6 : 1 }}>
      {values.map((v) => (
        <span key={v} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, padding: '2px 8px', borderRadius: 999, background: 'var(--surface-2)', color: 'var(--text-2)' }}>
          {v}<button disabled={disabled} onClick={() => onChange(values.filter((x) => x !== v))} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-4)', fontSize: 12, lineHeight: 1 }}>×</button>
        </span>
      ))}
      <input value={text} disabled={disabled} list={listId} placeholder={values.length ? '' : placeholder} onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(text); } else if (e.key === 'Backspace' && !text && values.length) onChange(values.slice(0, -1)); }}
        onBlur={() => add(text)}
        style={{ flex: 1, minWidth: 90, border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontSize: 12.5, padding: '2px 4px' }} />
    </div>
  );
};

// What the filters kept out of a preview: Apollo's count without them minus
// its count with them, plus what the checks on the fetched page dropped.
const PAGE_SKIPS = ['public', 'nonprofit', 'government', 'media', 'software', 'funded', 'subsidiary'];
const leftOut = (pv) => {
  const m = (pv && pv.meta) || {};
  const server = m.total_unfiltered != null && pv.total != null ? Math.max(0, m.total_unfiltered - pv.total) : 0;
  return server + PAGE_SKIPS.reduce((n, k) => n + (m[`skipped_${k}`] || 0), 0);
};
const leftOutDetail = (pv) => {
  const m = (pv && pv.meta) || {};
  const bits = [];
  if (m.total_unfiltered != null && pv.total != null && m.total_unfiltered > pv.total) bits.push(`${(m.total_unfiltered - pv.total).toLocaleString()} by Apollo`);
  PAGE_SKIPS.forEach((k) => { if (m[`skipped_${k}`]) bits.push(`${m[`skipped_${k}`]} ${k === 'public' ? 'public' : SKIP_SHORT[k]} on the first page`); });
  return bits.join(' · ');
};

// Find leads: a source, an industry, a geography, a size → a filled batch.
const FindPanel = ({ onCreated }) => {
  const [opts, setOpts] = React.useState(null);
  const [source, setSource] = React.useState('apollo');
  const [industryId, setIndustryId] = React.useState('');
  const [keywords, setKeywords] = React.useState([]);
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);
  const [ranges, setRanges] = React.useState([]);
  const [limit, setLimit] = React.useState(100);
  const [quiet, setQuiet] = React.useState(12);
  const [skip, setSkip] = React.useState(null);          // what the search leaves out; the server's defaults until loaded
  const [minYears, setMinYears] = React.useState(null);
  const [name, setName] = React.useState('');
  const [runNow, setRunNow] = React.useState(true);
  const [preview, setPreview] = React.useState(null);
  const [previewing, setPreviewing] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    window.AtlasApi.prospectingDiscoverOptions().then((o) => {
      setOpts(o);
      const s = o.sources || {};
      setSource(s.apollo ? 'apollo' : s.maps ? 'maps' : 'atlas');
      setSkip(o.skip_defaults || []);
      setMinYears(o.min_years_default != null ? o.min_years_default : 0);
    }).catch((e) => { setOpts({ sources: { atlas: true }, industries: [], employee_ranges: [] }); window.pushToast?.({ type: 'error', title: 'Could not load sources', body: e.message }); });
  }, []);
  const ind = opts && (opts.industries || []).find((i) => i.id === industryId);
  const prefill = (src, i) => {
    if (!i) return;
    setKeywords(src === 'maps' ? (i.maps || []) : (i.keywords || []));
    setRanges(i.employee_ranges || []);
  };
  // Combobox hands back an event-shaped { target: { value } }; a bare id is accepted too.
  const pickIndustry = (e) => { const id = e && e.target ? e.target.value : e; setIndustryId(id || ''); setPreview(null); prefill(source, (opts.industries || []).find((i) => i.id === id)); };
  const pickSource = (src) => { if (!opts || !opts.sources[src]) return; setSource(src); setPreview(null); prefill(src, ind); };
  const q = () => ({ source, industry_id: industryId || null, keywords, states, cities, employee_ranges: source === 'apollo' ? ranges : [], limit, quiet_months: quiet,
    ...(skip ? { skip } : {}), min_years: source === 'apollo' ? (minYears || 0) : 0 });
  const skipsHere = ((opts && opts.skips) || []).filter((x) => (x.sources || []).includes(source));
  const toggleSkip = (k) => { setSkip((cur) => ((cur || []).includes(k) ? cur.filter((x) => x !== k) : [...(cur || []), k])); setPreview(null); };
  const canRun = source === 'atlas' ? !!industryId : keywords.length > 0 && (source !== 'maps' || states.length || cities.length);
  const doPreview = async () => {
    setPreviewing(true);
    try { setPreview(await window.AtlasApi.prospectingDiscoverPreview(q())); }
    catch (e) { setPreview(null); window.pushToast?.({ type: 'error', title: 'Search failed', body: e.message }); }
    finally { setPreviewing(false); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const res = await window.AtlasApi.prospectingDiscover({ ...q(), name: name.trim() || undefined, run: runNow, steps: ['identify', 'ownership', 'qualify'] });
      const it = res.intake || {};
      window.pushToast?.({ type: 'success', title: `${it.added || 0} lead${it.added === 1 ? '' : 's'} added`, body: intakeBody(it, res.started ? 'pipeline running' : null) });
      onCreated(res.batch);
    } catch (e) {
      window.pushToast?.({ type: 'error', title: 'Could not create batch', body: e.message });
      setBusy(false);
    }
  };
  if (!opts) return <div style={{ fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>;
  const S = opts.sources || {};
  const srcBtn = (k, label) => (
    <button key={k} onClick={() => pickSource(k)} disabled={!S[k]} title={S[k] ? '' : 'Not configured'} style={chipStyle(source === k, !S[k])}>{label}</button>
  );
  const geo = window.GEO || { LIST_IDS: {} };
  return (
    <div>
      <window.Field label="Source"><div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{srcBtn('apollo', 'Apollo')}{srcBtn('maps', 'Google Maps')}{srcBtn('atlas', 'Atlas lead pool')}</div></window.Field>
      <window.Field label="Industry">
        <window.Combobox value={industryId} onChange={pickIndustry} searchable placeholder="Pick an industry"
          options={(opts.industries || []).map((i) => ({ value: i.id, label: i.name }))} />
      </window.Field>
      {source !== 'atlas' && (
        <window.Field label={source === 'maps' ? 'Categories' : 'Keywords'}>
          <ChipsInput values={keywords} onChange={(v) => { setKeywords(v); setPreview(null); }} placeholder={source === 'maps' ? 'roofing contractor' : 'roofing, roofing contractor'} />
        </window.Field>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: source === 'atlas' ? '1fr' : '1fr 1fr', gap: 10 }}>
        <window.Field label="States"><ChipsInput values={states} onChange={(v) => { setStates(v); setPreview(null); }} placeholder="Virginia, NC" listId={geo.LIST_IDS.state} /></window.Field>
        {source !== 'atlas' && <window.Field label="Cities"><ChipsInput values={cities} onChange={(v) => { setCities(v); setPreview(null); }} placeholder="Richmond" listId={geo.LIST_IDS.city} /></window.Field>}
      </div>
      {source === 'apollo' && (
        <window.Field label="Headcount">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(opts.employee_ranges || []).map((r) => <button key={r} style={chipStyle(ranges.includes(r))} onClick={() => { setRanges((x) => (x.includes(r) ? x.filter((y) => y !== r) : [...x, r])); setPreview(null); }}>{rangeLabel(r)}</button>)}
          </div>
        </window.Field>
      )}
      {skipsHere.length > 0 && skip && (
        <window.Field label="Leave out">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {skipsHere.map((x) => <button key={x.key} style={chipStyle(skip.includes(x.key))} onClick={() => toggleSkip(x.key)}>{x.label}</button>)}
          </div>
        </window.Field>
      )}
      {source === 'apollo' && minYears != null && (
        <window.Field label="In business">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {((opts && opts.years) || [0, 3, 5, 10]).map((y) => <button key={y} style={chipStyle(minYears === y)} onClick={() => { setMinYears(y); setPreview(null); }}>{y ? `${y}+ years` : 'Any'}</button>)}
          </div>
        </window.Field>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: source === 'atlas' ? '1fr 1fr' : '1fr', gap: 10 }}>
        {source === 'atlas' && (
          <window.Field label="Untouched for">
            <window.Picker value={quiet} onChange={(v) => { setQuiet(Number(v)); setPreview(null); }} width="100%" placeholder="Untouched for"
              options={[6, 12, 18, 24, 36].map((m) => ({ value: m, label: `${m} months` }))} />
          </window.Field>
        )}
        <window.Field label="Up to">
          <window.Picker value={limit} onChange={(v) => { setLimit(Number(v)); setPreview(null); }} width="100%" placeholder="Up to"
            options={[25, 50, 100, 250, 500].filter((n) => n <= (opts.max_limit || 500)).map((n) => ({ value: n, label: `${n} companies` }))} />
        </window.Field>
      </div>
      <window.Field label="Batch name"><window.TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder={ind ? `${ind.name} · ${states.join(', ') || 'anywhere'}` : 'Named after the search'} /></window.Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--text-2)', marginBottom: 12, cursor: 'pointer' }}>
        <input type="checkbox" checked={runNow} onChange={(e) => setRunNow(e.target.checked)} /> Read, check ownership and qualify right away
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <window.Btn size="sm" variant="outline" disabled={!canRun || previewing || busy} onClick={doPreview}>{previewing ? 'Searching…' : 'Preview'}</window.Btn>
        <window.Btn size="sm" variant="primary" disabled={!canRun || busy || previewing} onClick={create}>{busy ? 'Creating…' : `Create batch`}</window.Btn>
        {preview && (
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {preview.total != null ? `${Number(preview.total).toLocaleString()} match` : `${preview.sample.length} in the first look`}
            {preview.would_add != null ? ` · ${preview.would_add} will be added` : ''}
            {preview.meta && preview.meta.skipped_prospected ? ` · ${preview.meta.skipped_prospected} already prospected` : ''}
            {leftOut(preview) ? <span title={leftOutDetail(preview)} style={{ cursor: 'help' }}>{` · ${leftOut(preview).toLocaleString()} left out`}</span> : ''}
          </span>
        )}
      </div>
      {preview && preview.sample && preview.sample.length > 0 && (
        <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {preview.sample.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 10px', fontSize: 12, borderTop: i ? '1px solid var(--border)' : 'none' }}>
              <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
              <span style={{ color: 'var(--text-4)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.domain || 'no website'}</span>
              <span style={{ flex: 1 }} />
              <span style={{ color: 'var(--text-3)', fontSize: 11, whiteSpace: 'nowrap' }}>
                {[s.city && s.state ? `${s.city}, ${s.state}` : (s.state || null), s.google_rating != null ? `${s.google_rating}★ (${s.google_reviews || 0})` : null, s.employees ? `${s.employees} staff` : null, s.founded_year ? `since ${s.founded_year}` : null, s.phone || null].filter(Boolean).join(' · ')}
              </span>
            </div>
          ))}
        </div>
      )}
      {preview && preview.sample && preview.sample.length === 0 && <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-3)' }}>Nothing matched.</div>}
    </div>
  );
};

const NewBatchDrawer = ({ open, onClose, onCreated }) => {
  const [mode, setMode] = React.useState('find');
  const [name, setName] = React.useState('');
  const [text, setText] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  React.useEffect(() => { if (open) { setName(''); setText(''); setFile(null); setBusy(false); setErr(''); } }, [open]);
  React.useEffect(() => { setErr(''); }, [mode, text, file]);
  const lines = text.split('\n').filter((l) => l.trim()).length;
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const res = mode === 'file'
        ? await window.AtlasApi.prospectingUpload(file, name)
        : await window.AtlasApi.prospectingCreateBatch({ name: name.trim() || undefined, text });
      const it = res.intake;
      window.pushToast?.({ type: 'success', title: `Batch created · ${it.added.toLocaleString()} lead${it.added === 1 ? '' : 's'}`, body: intakeBody(it) });
      onCreated(res.batch);
    } catch (e) {
      // A refusal (too many leads, nothing usable) says what to change: it stays in the drawer until the list changes.
      setErr(errDetail(e));
      setBusy(false);
    }
  };
  const errLine = err && <div role="alert" style={{ marginTop: 8, fontSize: 12, color: 'var(--rose-fg, #c33)', overflowWrap: 'anywhere' }}>{err}</div>;
  const tab = (k, label) => <button key={k} onClick={() => setMode(k)} style={chipStyle(mode === k)}>{label}</button>;
  return (
    <window.Drawer open={open} onClose={onClose} title="New batch" width={560}
      footer={mode === 'find' ? null : (<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <window.Btn size="sm" variant="outline" onClick={onClose} disabled={busy}>Cancel</window.Btn>
        <window.Btn size="sm" variant="primary" disabled={busy || (mode === 'file' ? !file : !lines)} onClick={submit}>{busy ? 'Creating…' : (mode === 'file' ? 'Upload' : `Create · ${lines} line${lines === 1 ? '' : 's'}`)}</window.Btn>
      </div>)}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>{tab('find', 'Find leads')}{tab('paste', 'Paste a list')}{tab('file', 'Upload a file')}</div>
      {mode === 'find' && <FindPanel onCreated={onCreated} />}
      {mode === 'paste' && (
        <>
          <window.Field label="Batch name"><window.TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fire & Life Safety · Virginia · Oct" /></window.Field>
          <window.Field label="Leads" hint='Up to 500 per batch. "Acme Plumbing, acmeplumbing.com" gives both a name and a website. Emails work too — the domain is the lead.'>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} placeholder={'acmeplumbing.com\nBeta Roofing, betaroof.com\nGamma HVAC'}
              style={{ width: '100%', padding: '8px 10px', fontSize: 12.5, fontFamily: 'var(--font-mono)', background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 8, resize: 'vertical' }} />
            {errLine}
          </window.Field>
        </>
      )}
      {mode === 'file' && (
        <>
          <window.Field label="Batch name"><window.TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Named after the file" /></window.Field>
          <window.Field label="File" hint="CSV, XLSX or TXT, up to 500 leads. Columns named website / url / domain and company / name are read; a list of URLs needs no header.">
            <input type="file" accept=".csv,.xlsx,.xlsm,.txt" onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} style={{ fontSize: 12 }} />
            {errLine}
          </window.Field>
        </>
      )}
    </window.Drawer>
  );
};

const RunPanel = ({ batch, onStarted }) => {
  // Owners (Apollo credits) is off by default: the run only looks owners up on rows the rules passed, after a person turns the chip on.
  const [steps, setSteps] = React.useState(['identify', 'ownership', 'qualify', 'revenue']);
  const [busy, setBusy] = React.useState(false);
  const running = batch && batch.status === 'running';
  const prog = (batch && batch.run_progress) || {};
  const start = async () => {
    setBusy(true);
    // The thread flips the batch to 'running' a beat after the POST returns:
    // refetch now and again shortly, so the progress bar appears without a click.
    try { await window.AtlasApi.prospectingRun(batch.id, { steps }); onStarted(); setTimeout(onStarted, 1500); setTimeout(onStarted, 4000); }
    catch (e) { window.pushToast?.({ type: 'error', title: 'Could not start', body: e.message }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
      <span style={{ fontSize: 11, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>Run</span>
      {Object.entries(STEP_LABEL).map(([k, label]) => (
        <button key={k} style={chipStyle(steps.includes(k))} disabled={running}
          onClick={() => setSteps((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]))}>{label}</button>
      ))}
      <window.Btn size="sm" variant="primary" disabled={running || busy || !steps.length || !batch} onClick={start} style={{ marginLeft: 6 }}>{running ? 'Running…' : 'Run on open leads'}</window.Btn>
      {running && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: 'var(--text-3)' }}>
          <span style={{ width: 120, height: 6, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden' }}>
            <span style={{ display: 'block', height: '100%', width: `${prog.total ? Math.round(100 * (prog.done || 0) / prog.total) : 0}%`, background: 'var(--accent)' }} />
          </span>
          {prog.done || 0}/{prog.total || '?'}{prog.current ? ` · ${prog.current}` : ''}
        </span>
      )}
      {!running && batch && prog.stopped && <span style={{ fontSize: 11, color: 'var(--text-4)', marginLeft: 'auto' }}>Stopped early — run again for the rest</span>}
      {!running && batch && prog.apollo_stopped && <span style={{ fontSize: 11, color: 'var(--amber-fg, #b7791f)' }} title={prog.apollo_stopped}>Apollo stopped answering — owners not looked up</span>}
      {!running && batch && batch.run_error && <span style={{ fontSize: 11, color: 'var(--rose-fg, #c33)' }}>{batch.run_error}</span>}
    </div>
  );
};

// What was observed about the company and where it was seen — the size
// rules read these, never a dollar figure.
const observedFacts = (p) => {
  const o = p.observables || {};
  const s = p.seed || {};
  const id = p.identity || {};
  const out = [];
  const add = (label, value, source, href) => { if (value != null && value !== '') out.push({ label, value: String(value), source, href }); };
  add('LinkedIn', o.linkedin_headcount ? `${o.linkedin_headcount} employees` : null, 'Apollo', o.linkedin_url);
  if (o.google_rating != null) add('Google', `${o.google_rating}★ on ${o.google_reviews || 0} reviews${o.google_verified ? ' · verified' : ''}`, o.google_match === 'name' ? 'Google listing, matched by name only' : 'Google listing', o.google_maps_url);
  else if (s.google_rating != null) add('Google', `${s.google_rating}★ on ${s.google_reviews || 0} reviews`, 'Google Maps');
  add('Staff', o.web_headcount ? `${o.web_headcount}${o.web_headcount_to && o.web_headcount_to !== o.web_headcount ? `–${o.web_headcount_to}` : ''}` : null, 'business directories');
  add('Staff', id.employees_stated, 'the website');
  add('Staff', s.employees, 'SourceScrub');
  add('Locations', id.locations_count, 'the website');
  add('Locations', s.locations || null, 'Apollo');
  add('Founded', id.year_founded || o.founded_year || o.web_founded || s.founded_year || null,
    id.year_founded ? 'the website' : o.founded_year || s.founded_year ? 'Apollo' : 'business directories');
  add('Listed as', o.web_ownership_type, 'business directories');
  add('Owned by', o.apollo_owned_by_name, 'Apollo');
  add('Parent', o.apollo_parent_name && o.apollo_parent_name !== o.apollo_owned_by_name ? o.apollo_parent_name : null, 'Apollo');
  add('Funding', o.apollo_total_funding ? `$${Number(o.apollo_total_funding).toLocaleString()}` : null, 'Apollo');
  add('Ticker', o.apollo_public_symbol, 'Apollo');
  add('Emails on the web', (o.web_person_emails || []).length ? o.web_person_emails.slice(0, 4).join(', ') : null, 'business directories');
  return out;
};

// What the discovery source said beyond the facts: its category, a parent it knows of.
const seedNotes = (seed) => {
  const s = seed || {};
  return [s.category ? `listed as ${s.category}` : null, s.parent_org_id ? 'Apollo lists a parent company' : null].filter(Boolean).join(' · ');
};

const queryBits = (q) => (q ? [SOURCE_LABEL[q.source] || q.source, (q.keywords || []).join(', ') || null, (q.cities || []).length ? q.cities.join(', ') : null,
  (q.states || []).join(', ') || null,
  !(q.states || []).length && !(q.cities || []).length && (q.countries || []).length ? q.countries.map((c) => COUNTRY_SHORT[c] || c).join(' & ') : null,
  (q.employee_ranges || []).length ? `${rangesLabel(q.employee_ranges)} staff` : null,
  q.min_years ? `${q.min_years}+ years` : null,
  (q.skip || []).length ? `no ${q.skip.map((k) => SKIP_SHORT[k] || k).join(', ')}` : null,
  q.source === 'atlas' ? `untouched ${q.quiet_months} mo` : null].filter(Boolean) : []);

const peopleSource = (c) => ({ apollo: 'Apollo', 'site+apollo': 'the website + Apollo', 'site+web': 'the website + an email finder',
  'site+pattern': "the website + the domain's email pattern", 'site+atlas': 'the website + Atlas' }[c.source] || 'the website');
// 'Dan Worstell → danw@' — the people a domain's pattern was read off.
const patternExamples = (ep) => (ep.examples || []).map((x) => `${x.name} → ${x.email.split('@')[0]}@`).join(', ');

// The run, step by step, from what the row kept — the research wand's "How
// it got there". The ownership searches are listed for leads checked from
// 2026-10-08 on; older rows kept only the page they cite.
const whySteps = (p, batch) => {
  const out = [];
  const add = (kind, label, detail, links) => out.push({ kind, label, detail: detail || null, links: links || [] });
  const seed = p.seed || {};
  const ident = p.identity || {};
  const own = p.ownership || {};
  const st = p.atlas_state || {};
  const src = seed.source || (p.signal_id ? 'signals' : batch && batch.source);
  add('search', FOUND_LABEL[src] || 'Added to the batch',
    [batch && batch.query && batch.source === src ? queryBits(batch.query).slice(1).join(' · ') : null, seedNotes(seed)].filter(Boolean).join(' — '));
  if (p.in_atlas) add('atlas', p.atlas_match_tier === 'domain' ? 'In Atlas — same website' : 'In Atlas',
    [st.name, st.owner ? `owner ${st.owner}` : null, st.live_deals ? `${st.live_deals} live deal${st.live_deals === 1 ? '' : 's'}` : null].filter(Boolean).join(' · '));
  else if (sameName(p)) add('atlas', `Same name in Atlas: ${st.candidate}`, `Not linked — ${st.candidate_state ? `that one is in ${st.candidate_state}` : 'no location to match on'}`);
  else add('atlas', 'Not in Atlas');
  const fails = readFailures(p);
  if (ident.from === 'seller_profile') add('read', 'Used the seller brief Atlas already had');
  else if (ident.from === 'company_record') add('read', 'Used the Atlas company record');
  else if ((ident.page_urls || []).length) add('read', `Read ${ident.page_urls.length} page${ident.page_urls.length === 1 ? '' : 's'} of ${p.domain || 'the website'}`, null, ident.page_urls.map((u) => ({ url: u, title: hostPath(u) })));
  else if (ident.note) add('read', cap(ident.note));
  if (fails && !p.identity) add('error', `The website didn't load (${fails} of 3 tries)`);
  if (p.observables) {
    const seen = observedFacts(p).filter((f) => ['LinkedIn', 'Google', 'Staff', 'Founded', 'Listed as'].includes(f.label));
    add('search', 'Checked LinkedIn, Google and business directories', seen.length ? seen.map((f) => `${f.label} ${f.value}`).join(' · ') : 'Nothing found');
  }
  const trail = own.trail || [];
  [1, 2].forEach((n) => {
    const t = trail.filter((x) => x.step === n);
    if (!t.length) return;
    if (n === 2) add('think', `Checked who owns ${own.acquired_by || 'the parent'}`);
    const reads = t.filter((x) => x.kind === 'read');
    if (reads.length) add('read', `Read ${reads.length} page${reads.length === 1 ? '' : 's'} for ownership history`, null, reads.map((x) => ({ url: x.url, title: hostPath(x.url) })));
    t.filter((x) => x.kind === 'search').forEach((x) => add('search', `Searched the web: ${x.query}`, `${x.hits.length} result${x.hits.length === 1 ? '' : 's'}`,
      x.hits.map((h) => ({ url: h.url, title: h.title || hostPath(h.url) }))));
  });
  if (own.outcome) add('think', `Ownership: ${ownLabel(own)}`,
    [own.confidence ? `${cap(own.confidence)} confidence` : null, own.evidence_url ? `cites ${hostOf(own.evidence_url)}` : null].filter(Boolean).join(' · '));
  if (p.industry_reason || p.industry_proposed) {
    const nm = p.industry_name || p.industry_proposed;
    const judged = p.industry_confidence != null && p.industry_reason !== 'set by hand';
    add('think', p.industry_reason === 'set by hand' ? `Industry set by hand: ${nm}`
      : !p.industry_id ? `No industry rule fits${p.industry_proposed ? ` — closest: ${p.industry_proposed}` : ''}`
        : judged ? `Industry: ${nm} · ${pct(p.industry_confidence)} match` : `Industry: ${nm}`,
    !judged && p.industry_id && p.industry_reason && p.industry_reason !== 'set by hand' ? cap(p.industry_reason) : null);
  }
  if (p.geo_fit) add('think', `Geography: ${GEO_LABEL[p.geo_fit] || p.geo_fit}`, p.geo_reason);
  if (p.size_tier) add('think', `Size: ${TIER_LABEL[p.size_tier] || p.size_tier}`, p.industry_id ? `${p.industry_name || p.industry_proposed} criteria` : null);
  const est = p.revenue_estimate || {};
  (est.trail || []).filter((x) => x.kind === 'search').forEach((x) => add('search', `Searched the web: ${x.query}`, `${x.hits.length} result${x.hits.length === 1 ? '' : 's'}`,
    x.hits.map((h) => ({ url: h.url, title: h.title || hostPath(h.url) }))));
  const revReads = (est.trail || []).filter((x) => x.kind === 'read');
  if (revReads.length) add('read', `Read ${revReads.length} page${revReads.length === 1 ? '' : 's'} for revenue figures`, null, revReads.map((x) => ({ url: x.url, title: hostPath(x.url) })));
  if (est.status === 'estimated') add('think', `Revenue: ${revLabel(est)}`, [`${cap(est.confidence)} confidence`, (est.methods || []).length ? `${est.methods.length} methods` : null].filter(Boolean).join(' · '));
  else if (est.status === 'unable') add('think', 'Revenue: not enough to estimate');
  const people = (p.contacts || []).filter((c) => c.name);
  if (people.length) {
    const emails = people.filter(hasEmail).length;
    const guesses = people.filter(isGuess).length;
    add('people', `Found ${people.length} ${people.length === 1 ? 'person' : 'people'}, ${emails} with an email${guesses ? `, ${guesses} guessed` : ''}`, `from ${[...new Set(people.map(peopleSource))].join(', ')}`);
    people.filter((c) => c.email_pattern).forEach((c) => add('think', c.source === 'site+pattern' ? `Guessed ${c.name}'s address: ${c.email}` : c.source === 'site+atlas' ? `${c.name} is already in Atlas: ${c.email}` : `No address pattern for ${c.name}`,
      [c.email_pattern.basis, patternExamples(c.email_pattern)].filter(Boolean).join(' · ')));
  }
  if (p.stage === 'rejected' && p.decision !== 'rejected' && p.stage_note) add('error', `Rejected — ${p.stage_note}`);
  if (p.decision && p.decided_at) add('user', `${p.decision === 'approved' ? 'Approved' : 'Rejected'}${p.decided_by_name ? ` by ${p.decided_by_name}` : ''}`, window.fmtDate(p.decided_at));
  if (p.outreach && p.outreach.sent_at) add('user', `Sent — ${sentCounts(p.outreach).text}`, window.fmtDate(p.outreach.sent_at));
  return out;
};

const WhySection = ({ id, title, children }) => (
  <section data-why={id} style={{ paddingTop: 14, marginTop: 14, borderTop: '1px solid var(--border)' }}>
    {title && <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>{title}</div>}
    {children}
  </section>
);

const WhyQuote = ({ text, url }) => ((text || url) ? (
  <div style={{ marginTop: 6 }}>
    {text && <div style={{ color: 'var(--text-3)', fontStyle: 'italic', wordBreak: 'break-word' }}>“{text}”</div>}
    {url && <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11.5, color: 'var(--accent-text)', wordBreak: 'break-all' }}>{hostPath(url)}</a>}
  </div>
) : null);

const PILL_ROW = { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' };
const MUTED = { color: 'var(--text-4)' };
const LBL = { color: 'var(--text-4)', marginRight: 6 };

// Everything behind one row's verdicts: each with its reasoning and source,
// the facts the size rules read, what the website said, and the steps.
const ProspectWhy = ({ p, batch, focus, goToCompany, onClose, onChange, onPrev, onNext, onRetry, onEstimate, running }) => {
  const last = React.useRef(null);
  if (p) last.current = p;
  const row = p || last.current;
  const [full, setFull] = React.useState(null);
  const [showSteps, setShowSteps] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [body, setBody] = React.useState(null);
  const rid = p ? p.id : null;
  const rver = p ? p.updated_at : null;
  // The batch list leaves the ownership trail, the size checks and the revenue work out; the row in full has them.
  React.useEffect(() => {
    if (!rid) return undefined;
    let live = true;
    window.AtlasApi.prospectingProspect(rid).then((r) => { if (live) setFull(r); }).catch(() => {});
    return () => { live = false; };
  }, [rid, rver]);
  React.useEffect(() => {
    if (!rid || !body) return;
    const el = focus ? body.querySelector(`[data-why="${focus}"]`) : null;
    if (el) el.scrollIntoView({ block: 'start' });
    else if (body.parentElement) body.parentElement.scrollTop = 0;
  }, [rid, focus, body]);
  if (!row) return null;
  const d = full && full.id === row.id
    ? { ...row, ownership: full.ownership || row.ownership, size_detail: full.size_detail || row.size_detail, revenue_estimate: full.revenue_estimate || row.revenue_estimate }
    : row;
  const est = d.revenue_estimate || null;
  const ident = d.identity || {};
  const own = d.ownership || {};
  const st = d.atlas_state || {};
  const seed = d.seed || {};
  const name = ident.company_name || d.input_name || d.domain || '—';
  const where = ident.hq_city || ident.hq_state ? [ident.hq_city, ident.hq_state].filter(Boolean).join(', ') : [seed.city, seed.state].filter(Boolean).join(', ');
  const people = (d.contacts || []).filter((c) => c.name);
  const facts = observedFacts(d);
  const steps = whySteps(d, batch);
  const fails = readFailures(d);
  const openRow = d.stage !== 'sent' && d.stage !== 'rejected';
  const canFindOwners = openRow && !people.some(hasEmail) && (d.domain || (ident.people || []).length);
  const lastErr = (d.errors || []).length ? d.errors[d.errors.length - 1] : null;
  const notYet = (t) => <div style={MUTED}>{t}{openRow ? ' yet' : ''}.</div>;
  const siteLine = [ident.customer_type && ident.customer_type !== 'unknown' ? ident.customer_type : null,
    ident.service_area && ident.service_area.length ? `serves ${ident.service_area.slice(0, 6).join(', ')}` : null].filter(Boolean).join(' · ');
  const act = async (fn, label) => {
    setBusy(true);
    try { const r = await fn(); onChange(r && r.prospect ? r.prospect : r); if (typeof label === 'function') label(r); else if (label) window.pushToast?.({ type: 'success', title: label }); }
    catch (e) { window.pushToast?.({ type: 'error', title: 'Failed', body: e.message }); }
    finally { setBusy(false); }
  };
  const decision = d.stage === 'sent'
    ? <window.Pill hue={sentCounts(d.outreach).none ? 'amber' : 'green'}>Sent · {sentCounts(d.outreach).text}</window.Pill>
    : d.stage === 'rejected'
      ? <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => act(() => window.AtlasApi.prospectingDecision(d.id, { decision: null }), 'Reopened')}>Reopen</window.Btn>
      : (
        <>
          <window.Btn size="sm" variant="ghost" disabled={busy} onClick={() => act(() => window.AtlasApi.prospectingDecision(d.id, { decision: 'rejected' }), 'Rejected')}>Reject</window.Btn>
          {d.decision === 'approved'
            ? <window.Btn size="sm" variant="primary" disabled={busy} onClick={() => act(() => window.AtlasApi.prospectingSend(d.id), sentToast)}>Send</window.Btn>
            : <window.Btn size="sm" variant="primary" disabled={busy || !d.identity} title={!d.identity ? 'Read the site first' : ''} onClick={() => act(() => window.AtlasApi.prospectingDecision(d.id, { decision: 'approved' }), 'Approved')}>Approve</window.Btn>}
        </>
      );
  return (
    <window.Drawer open={!!p} onClose={onClose} width={560} title={name}
      subtitle={[d.domain || 'no website', where, d.in_atlas ? 'in Atlas' : 'new to Atlas'].filter(Boolean).join(' · ')}
      footer={(
        <>
          <window.Btn size="sm" variant="ghost" disabled={!onPrev} onClick={onPrev} title="Previous lead" icon={<window.Icon.ChevronL size={12} />} />
          <window.Btn size="sm" variant="ghost" disabled={!onNext} onClick={onNext} title="Next lead" icon={<window.Icon.ChevronR size={12} />} />
          <span style={{ flex: 1 }} />
          {decision}
        </>
      )}>
      <div ref={setBody} style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-2)' }}>
        <div style={PILL_ROW}>
          <window.Pill hue={d.stage === 'rejected' ? 'rose' : d.stage === 'sent' ? 'green' : 'slate'} sz="sm">{STAGE_LABEL[d.stage] || d.stage}</window.Pill>
          {d.decision === 'approved' && d.stage !== 'sent' && <window.Pill hue="blue" sz="sm">Approved{d.decided_by_name ? ` by ${d.decided_by_name}` : ''}</window.Pill>}
          {d.stage === 'rejected' && d.stage_note && <span style={{ color: 'var(--text-3)' }}>{cap(d.stage_note)}</span>}
        </div>
        {d.stage === 'intake' && fails > 0 && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--rose-fg, #c33)' }}>
            <span>The website didn't load — {fails} of 3 tries.</span>
            {!running && <window.Btn size="xs" variant="outline" onClick={() => onRetry(d.id)}>Retry</window.Btn>}
          </div>
        )}
        {lastErr && !(d.stage === 'intake' && fails) && <div style={{ marginTop: 8, color: 'var(--rose-fg, #c33)' }}>{lastErr.error}</div>}

        {d.stage === 'sent' && d.outreach && d.outreach.people && (
          <WhySection id="outreach" title="Outreach">
            {d.outreach.people.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {d.outreach.people.map((x) => {
                  const em = EMAIL_OUTCOME(x, d.outreach.campaign);
                  const ca = CALL_OUTCOME[x.call] || CALL_OUTCOME.no_phone;
                  return (
                    <div key={x.contact_id}>
                      <div><span style={{ fontWeight: 500, color: 'var(--text)' }}>{x.name || '—'}</span>{x.title ? <span style={MUTED}> · {x.title}</span> : null}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4, marginTop: 2 }}>
                        <window.Pill hue={em.hue} sz="sm">{em.label}</window.Pill>
                        <window.Pill hue={ca.hue} sz="sm" title={x.call_why || undefined}>{ca.label}</window.Pill>
                        {(x.why || []).length > 0 && <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{x.why.join(' · ')}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <div style={MUTED}>Nobody to reach.</div>}
          </WhySection>
        )}

        <WhySection id="ownership" title="Ownership">
          {own.outcome ? (
            <>
              <div style={PILL_ROW}>
                <window.Pill hue={OWN_HUE[own.outcome] || 'slate'}>{ownLabel(own)}</window.Pill>
                {own.confidence && <window.Pill hue={CONF_HUE[own.confidence] || 'slate'} sz="sm">{cap(own.confidence)} confidence</window.Pill>}
              </div>
              {own.acquired_by && (
                <div style={{ marginTop: 6 }}>
                  {own.reason === 'subsidiary' ? 'Part of ' : 'Acquired by '}<b>{own.acquired_by}</b>
                  {own.ultimate_parent && own.ultimate_parent !== own.acquired_by ? <>, ultimately <b>{own.ultimate_parent}</b></> : null}
                  {own.ultimate_parent_status && own.ultimate_parent_status !== 'unknown' ? ` — ${PARENT_LABEL[own.ultimate_parent_status] || own.ultimate_parent_status}` : own.parent_checked ? ' — its owner could not be confirmed' : ''}
                </div>
              )}
              {own.reasoning && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{own.reasoning}</div>}
              <WhyQuote text={own.evidence_text} url={own.evidence_url} />
              {(own.step2_reasoning || own.step2_evidence_url) && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontWeight: 500, color: 'var(--text)' }}>The owner{own.step2_confidence ? <span style={{ ...MUTED, fontWeight: 400 }}> · {own.step2_confidence} confidence</span> : null}</div>
                  {own.step2_reasoning && <div style={{ whiteSpace: 'pre-wrap' }}>{own.step2_reasoning}</div>}
                  <WhyQuote text={own.step2_evidence_text} url={own.step2_evidence_url} />
                </div>
              )}
            </>
          ) : notYet('Not checked')}
        </WhySection>

        <WhySection id="industry" title="Industry">
          {d.industry_id || d.industry_proposed ? (
            <>
              <div style={PILL_ROW}>
                <window.Pill hue={d.industry_id ? 'blue' : 'slate'}>{d.industry_id ? (d.industry_name || d.industry_proposed) : 'No industry rule fits'}</window.Pill>
                {d.industry_id && d.industry_confidence != null && d.industry_reason !== 'set by hand' && (
                  <window.Pill hue={d.industry_confidence < 0.4 ? 'rose' : d.industry_confidence < 0.7 ? 'amber' : 'green'} sz="sm">{pct(d.industry_confidence)} match{d.industry_confidence < 0.4 ? ' · review' : ''}</window.Pill>
                )}
                {!d.industry_id && <window.Pill hue="rose" sz="sm">Review</window.Pill>}
              </div>
              {!d.industry_id && d.industry_proposed && <div style={{ marginTop: 6 }}><span style={LBL}>Closest</span>{d.industry_proposed}</div>}
              {d.industry_reason && <div style={{ marginTop: 6 }}>{d.industry_reason === 'set by hand' ? 'Set by hand.' : cap(d.industry_reason)}</div>}
            </>
          ) : notYet('Not classified')}
        </WhySection>

        <WhySection id="geo" title="Geography">
          {d.geo_fit ? (
            <>
              <window.Pill hue={GEO_HUE[d.geo_fit] || 'slate'}>{GEO_LABEL[d.geo_fit] || d.geo_fit}</window.Pill>
              {d.size_detail && window.LOCATION_TIER_LABEL && window.LOCATION_TIER_LABEL[d.size_detail.location_tier] && (
                <span style={{ ...MUTED, marginLeft: 8 }}>{window.LOCATION_TIER_LABEL[d.size_detail.location_tier]}</span>
              )}
              {d.geo_reason && <div style={{ marginTop: 6 }}>{d.geo_reason}</div>}
            </>
          ) : notYet('Not checked')}
        </WhySection>

        <WhySection id="size" title="Size">
          {d.size_tier ? (
            <>
              <window.Pill hue={TIER_HUE[d.size_tier] || 'slate'}>{TIER_LABEL[d.size_tier] || d.size_tier}</window.Pill>
              {d.size_detail && d.size_detail.confidence && <span style={{ ...MUTED, marginLeft: 8 }}>{d.size_detail.confidence} confidence</span>}
              {(d.size_detail && (d.size_detail.signals || []).length) || (d.size_reasons || []).length > 0
                ? <window.SizeChecks detail={d.size_detail} reasons={d.size_reasons} />
                : d.qualification_reason && <div style={{ marginTop: 6 }}>{cap(d.qualification_reason)}</div>}
            </>
          ) : notYet('Not sized')}
          {facts.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ ...MUTED, fontSize: 11, marginBottom: 4 }}>Observed</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 12, rowGap: 3 }}>
                {facts.map((f, i) => (
                  <React.Fragment key={i}>
                    <span style={{ color: 'var(--text-3)' }}>{f.label}</span>
                    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>
                      {f.value}{' '}
                      <span style={MUTED}>· {f.href ? <a href={f.href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{f.source}</a> : f.source}</span>
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </WhySection>

        <WhySection id="revenue" title="Revenue estimate">
          {est && est.status === 'estimated' ? (
            <>
              <div style={PILL_ROW}>
                <span style={{ fontSize: 17, fontWeight: 650, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{revLabel(est)}</span>
                <window.Pill hue={CONF_HUE[est.confidence] || 'slate'} sz="sm">{cap(est.confidence)} confidence</window.Pill>
              </div>
              <window.RevenueEstimate.Range est={est} />
              {est.reasoning && <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{est.reasoning}</div>}
              <window.RevenueEstimate.Methods est={est} />
            </>
          ) : est && est.status === 'unable' ? <div style={MUTED}>Not enough to estimate.</div> : notYet('Not estimated')}
          {openRow && d.identity && onEstimate && (
            <div style={{ marginTop: 8 }}>
              <window.Btn size="xs" variant="outline" disabled={running} onClick={() => onEstimate(d.id)}>{est ? 'Estimate again' : 'Estimate revenue'}</window.Btn>
            </div>
          )}
        </WhySection>

        <WhySection id="owners" title="Owners">
          {people.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {people.map((c, i) => (
                <div key={i}>
                  <div><span style={{ fontWeight: 500, color: 'var(--text)' }}>{c.name}</span>{c.title ? <span style={MUTED}> · {c.title}</span> : null}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                    {isGuess(c)
                      ? <>{c.email} <window.Pill hue={c.email_status === 'invalid' ? 'rose' : 'amber'} sz="sm"
                          title={c.email_status === 'invalid' ? 'The domain has no mail server' : 'Built from the pattern, not checked — not saved on the contact when sent'}>
                          {c.email_status === 'invalid' ? 'No mail server' : 'Guess'}</window.Pill></>
                      : c.email ? `${c.email}${c.email_status ? ` (${c.email_status})` : ''}` : 'no email found'}
                    {c.phone ? ` · ${c.phone}${c.phone_source === 'company' ? ' (company line)' : ''}` : ''}
                    {c.linkedin ? <> · <a href={c.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>LinkedIn</a></> : null}
                    <span style={MUTED}> · {peopleSource(c)}</span>
                  </div>
                  {c.email_pattern && (
                    <div style={{ fontSize: 11.5, color: 'var(--text-4)' }}>
                      {c.email_pattern.basis}{(c.email_pattern.examples || []).length ? ` · ${patternExamples(c.email_pattern)}` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (ident.people || []).length ? <div style={MUTED}>{ident.people.length} named on the website — not looked up{openRow ? ' yet' : ''}.</div> : notYet('Not looked up')}
          {(d.contacts || []).some((c) => c.error) && <div style={{ ...MUTED, marginTop: 4 }}>{d.contacts.filter((c) => c.error).map((c) => c.error).join(' · ')}</div>}
          {canFindOwners && <div style={{ marginTop: 8 }}><window.Btn size="xs" variant="outline" disabled={busy} onClick={() => act(() => window.AtlasApi.prospectingFindOwners(d.id), 'Owners looked up')}>Find owners (Apollo)</window.Btn></div>}
        </WhySection>

        <WhySection id="site" title="Website">
          {ident.from ? <div style={MUTED}>{ident.from === 'seller_profile' ? 'From the seller brief Atlas already had.' : 'From the Atlas company record.'}</div>
            : !d.identity ? (fails ? <div style={MUTED}>Didn't load — {fails} of 3 tries.</div> : notYet('Not read'))
              : ident.note ? <div style={MUTED}>{cap(ident.note)}.</div> : null}
          {ident.description && <div style={{ marginTop: 4 }}>{ident.description}</div>}
          {(ident.services || []).length > 0 && <div style={{ marginTop: 6 }}><span style={LBL}>Services</span>{ident.services.join(', ')}</div>}
          {siteLine && <div style={{ marginTop: 6, color: 'var(--text-3)' }}>{cap(siteLine)}</div>}
          {ident.ownership_mentions && <WhyQuote text={ident.ownership_mentions} />}
          {(ident.people || []).length > 0 && <div style={{ marginTop: 6 }}><span style={LBL}>Named</span>{ident.people.map((x) => `${x.name}${x.title ? ` (${x.title})` : ''}`).join(', ')}</div>}
          {(ident.page_urls || []).length > 0 && (
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: '2px 10px' }}>
              <span style={LBL}>Read</span>
              {ident.page_urls.map((u) => <a key={u} href={u} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{hostPath(u)}</a>)}
            </div>
          )}
          {d.identity && !ident.from && ident.confidence && <div style={{ marginTop: 8 }}><window.Pill hue={CONF_HUE[ident.confidence] || 'slate'} sz="sm">{cap(ident.confidence)} confidence</window.Pill></div>}
        </WhySection>

        {(d.in_atlas || sameName(d)) && (
          <WhySection id="atlas" title="Atlas">
            {d.in_atlas ? (
              <div>
                {d.company_id && goToCompany
                  ? <button onClick={() => goToCompany(d.company_id)} style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12.5, fontWeight: 500 }}>{st.name || d.company_name || name}</button>
                  : <b>{st.name || d.company_name || name}</b>}
                <span style={{ color: 'var(--text-3)' }}>
                  {st.pipeline_state ? ` · ${String(st.pipeline_state).replace(/_/g, ' ')}` : ''} · owner {st.owner || '—'} · {st.live_deals || 0} live deal{st.live_deals === 1 ? '' : 's'}{st.last_note ? ` · last note ${window.fmtDate(st.last_note)}` : ''}
                </span>
              </div>
            ) : <div>Same name in Atlas: <b>{st.candidate}</b>{st.candidate_state ? ` — in ${st.candidate_state}, not linked` : ' — not linked'}</div>}
          </WhySection>
        )}

        <WhySection id="steps">
          <button onClick={() => setShowSteps((v) => !v)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' }}>
            {showSteps ? <window.Icon.ChevronD size={10} /> : <window.Icon.ChevronR size={10} />}
            How it got here · {steps.length} step{steps.length === 1 ? '' : 's'}
          </button>
          {showSteps && <div style={{ marginTop: 4 }}><window.RunSteps steps={steps} gap={8} plain /></div>}
        </WhySection>
      </div>
    </window.Drawer>
  );
};

const PLAIN_BTN = { background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', maxWidth: '100%', font: 'inherit', color: 'inherit', textAlign: 'left' };

const ProspectRow = ({ p, industries, goToCompany, onChange, selected, onToggle, onWhy, onRetry, batchSource, running }) => {
  const [busy, setBusy] = React.useState(false);
  const ident = p.identity || {};
  const own = p.ownership || {};
  const seed = p.seed || {};
  const name = ident.company_name || p.input_name || p.domain || '—';
  const act = async (fn, label) => {
    setBusy(true);
    try { const r = await fn(); onChange(r && r.prospect ? r.prospect : r); if (typeof label === 'function') label(r); else if (label) window.pushToast?.({ type: 'success', title: label }); }
    catch (e) { window.pushToast?.({ type: 'error', title: 'Failed', body: e.message }); }
    finally { setBusy(false); }
  };
  const stop = (e) => e.stopPropagation();
  const why = (focus) => (e) => { e.stopPropagation(); onWhy(p.id, focus); };
  const people = (p.contacts || []).filter((c) => c.name);
  const emails = people.filter(hasEmail).length;
  const guesses = people.filter(isGuess).length;
  const openRow = p.stage !== 'sent' && p.stage !== 'rejected';
  const canFindOwners = openRow && !people.some(hasEmail) && (p.domain || (ident.people || []).length);
  const where = ident.hq_city || ident.hq_state ? [ident.hq_city, ident.hq_state].filter(Boolean).join(', ') : [seed.city, seed.state].filter(Boolean).join(', ');
  // Where a row came from shows only when it differs from its batch (a Signals row in a pasted batch).
  const via = seed.source && seed.source !== batchSource ? (SOURCE_SHORT[seed.source] || SOURCE_LABEL[seed.source] || seed.source) : null;
  const fails = readFailures(p);
  // Set aside before the rules ran (ownership DQ, not a business, a live deal): the reason, not empty columns.
  const unjudged = p.stage === 'rejected' && !p.size_tier && !p.geo_fit;
  const dash = <span style={{ color: 'var(--text-4)' }}>—</span>;
  const pillBtn = (focus, pill) => <button onClick={why(focus)} title="How it got here" style={PLAIN_BTN}>{pill}</button>;
  return (
    <div className="prospect-row" data-sel={selected ? '1' : '0'} onClick={() => onWhy(p.id)} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
      <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, alignItems: 'center', padding: '8px 12px', fontSize: 12.5 }}>
        <div onClick={stop}><input type="checkbox" checked={!!selected} disabled={!openRow} onChange={() => onToggle(p.id)} style={{ margin: 0 }} /></div>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center', minWidth: 0 }}>
            <span title={name} style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{name}</span>
            <button className="why-btn" onClick={why(null)} title="How it got here" aria-label={`How ${name} got here`}
              style={{ ...PLAIN_BTN, flexShrink: 0, justifyContent: 'center', width: 18, height: 18, borderRadius: 4, color: 'var(--text-4)' }}>
              <window.Icon.Sparkle size={11} />
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {p.domain ? <a href={`https://${p.domain}`} target="_blank" rel="noreferrer" onClick={stop} style={{ color: 'var(--text-3)' }}>{p.domain}</a> : 'no website'}
            {where ? ` · ${where}` : ''}{via ? ` · via ${via}` : ''}{p.signal_id && batchSource !== 'signals' ? ' · web signal' : ''}
          </div>
        </div>
        <div>
          {p.in_atlas
            ? <button onClick={(e) => { e.stopPropagation(); if (p.company_id && goToCompany) goToCompany(p.company_id); }} title={`Open in Atlas${(p.atlas_state || {}).owner ? ` · owner ${p.atlas_state.owner}` : ''}`} style={PLAIN_BTN}><window.Pill hue="blue" sz="sm">In Atlas</window.Pill></button>
            : <span style={{ fontSize: 11.5, color: 'var(--text-4)' }}>New</span>}
        </div>
        {p.stage === 'intake' ? (
          <div style={{ gridColumn: '4 / 10', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontSize: 12, color: fails ? 'var(--rose-fg, #c33)' : 'var(--text-4)' }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fails ? `Website didn't load · ${fails} of 3 tries` : running ? 'Waiting for the run' : 'Not read yet'}</span>
            {fails > 0 && !running && <window.Btn size="xs" variant="outline" onClick={(e) => { e.stopPropagation(); onRetry(p.id); }}>Retry</window.Btn>}
          </div>
        ) : (
          <>
            <div style={{ minWidth: 0 }}>{own.outcome ? pillBtn('ownership', <window.Pill hue={OWN_HUE[own.outcome] || 'slate'} sz="sm" maxW={136}>{ownLabel(own)}</window.Pill>) : dash}</div>
            {unjudged ? (
              <div title={p.stage_note || ''} style={{ gridColumn: '5 / 10', minWidth: 0, fontSize: 12, color: 'var(--text-4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cap(p.stage_note) || 'Rejected'}</div>
            ) : (
              <>
                <div onClick={stop} style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <window.Picker value={p.industry_id || ''} disabled={busy || p.stage === 'sent'} title={p.industry_name || p.industry_proposed || 'Pick an industry'}
                    onChange={(v) => act(() => window.AtlasApi.prospectingDecision(p.id, { decision: p.decision || null, industry_id: v || null }), 'Industry set')}
                    size="sm" style={{ flex: 1, minWidth: 0 }} menuMinWidth={240} placeholder="Industry"
                    emptyLabel={p.industry_proposed ? `? ${p.industry_proposed}` : '— no industry —'}
                    options={industries.map((i) => ({ value: String(i.id), label: i.name }))} />
                  {p.industry_confidence != null && p.industry_reason !== 'set by hand' && (
                    <button onClick={why('industry')} title="How it got here"
                      style={{ ...PLAIN_BTN, flexShrink: 0, fontSize: 11, fontVariantNumeric: 'tabular-nums', color: !p.industry_id || p.industry_confidence < 0.4 ? 'var(--rose-fg, #c33)' : 'var(--text-4)' }}>{p.industry_id ? pct(p.industry_confidence) : 'review'}</button>
                  )}
                </div>
                <div>{p.geo_fit ? pillBtn('geo', <window.Pill hue={GEO_HUE[p.geo_fit] || 'slate'} sz="sm">{GEO_LABEL[p.geo_fit] || p.geo_fit}</window.Pill>) : dash}</div>
                <div>{p.size_tier ? pillBtn('size', <window.Pill hue={TIER_HUE[p.size_tier] || 'slate'} sz="sm">{TIER_LABEL[p.size_tier] || p.size_tier}</window.Pill>) : dash}</div>
                <div style={{ minWidth: 0 }}>
                  {revLabel(p.revenue_estimate)
                    ? <button onClick={why('revenue')} title={`${cap(p.revenue_estimate.confidence)} confidence`} style={{ ...PLAIN_BTN, fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text)' }}>{revLabel(p.revenue_estimate)}</button>
                    : p.revenue_estimate && p.revenue_estimate.status === 'unable' ? <button onClick={why('revenue')} style={{ ...PLAIN_BTN, fontSize: 12, color: 'var(--text-4)' }}>Unknown</button> : dash}
                </div>
                <div style={{ fontSize: 12, minWidth: 0 }}>
                  {people.length
                    ? (
                      <button onClick={why('owners')} title="How it got here" style={{ ...PLAIN_BTN, display: 'block', fontSize: 12, color: 'var(--text)' }}>
                        {people.length} {people.length === 1 ? 'person' : 'people'}
                        <div style={{ fontSize: 11, color: 'var(--text-4)' }}>{emails ? `${emails} email${emails === 1 ? '' : 's'}` : guesses ? `${guesses} guessed` : 'no emails'}</div>
                      </button>
                    )
                    : canFindOwners
                      ? <window.Btn size="xs" variant="ghost" disabled={busy} title="Apollo: owners, founders and C-suite at this domain" onClick={(e) => { e.stopPropagation(); act(() => window.AtlasApi.prospectingFindOwners(p.id), 'Owners looked up'); }}>Find owners</window.Btn>
                      : <span style={{ color: 'var(--text-4)' }}>{(ident.people || []).length ? `${ident.people.length} named` : '—'}</span>}
                </div>
              </>
            )}
          </>
        )}
        <div onClick={stop} style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {p.stage === 'sent' ? <window.Pill hue={sentCounts(p.outreach).none ? 'amber' : 'green'} sz="sm">Sent · {sentCounts(p.outreach).text}</window.Pill> : p.stage === 'rejected' ? (
            <>
              <window.Pill hue="rose" sz="sm" title={p.stage_note || ''}>Rejected</window.Pill>
              <window.Btn size="xs" variant="ghost" disabled={busy} onClick={() => act(() => window.AtlasApi.prospectingDecision(p.id, { decision: null }), 'Reopened')}>Reopen</window.Btn>
            </>
          ) : (
            <>
              {p.decision === 'approved'
                ? <window.Btn size="xs" variant="primary" disabled={busy} onClick={() => act(() => window.AtlasApi.prospectingSend(p.id), sentToast)}>Send</window.Btn>
                : <window.Btn size="xs" variant="outline" disabled={busy || !p.identity} title={!p.identity ? 'Read the site first' : 'Approve — then Send hands the owners to the Email Copilot; calls follow the emails if they don’t reply'} onClick={() => act(() => window.AtlasApi.prospectingDecision(p.id, { decision: 'approved' }), 'Approved')}>Approve</window.Btn>}
              <window.Btn size="xs" variant="ghost" disabled={busy} onClick={() => act(() => window.AtlasApi.prospectingDecision(p.id, { decision: 'rejected' }), 'Rejected')}>Reject</window.Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const QueryPills = ({ q }) => {
  const bits = queryBits(q);
  if (!bits.length) return null;
  return <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{bits.map((b, i) => <window.Pill key={i} hue="slate" sz="sm" maxW={320} title={b}>{b}</window.Pill>)}</div>;
};

const ProspectingView = ({ goToCompany }) => {
  const [batches, setBatches] = React.useState(null);
  const [batchId, setBatchId] = React.useState(null);
  const [batch, setBatch] = React.useState(null);
  const [items, setItems] = React.useState(null);
  const [funnel, setFunnel] = React.useState(null);
  const [stage, setStage] = React.useState('');
  const [tier, setTier] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState(() => new Set());
  const [industries, setIndustries] = React.useState([]);
  const [creating, setCreating] = React.useState(false);
  const [renaming, setRenaming] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [loadError, setLoadError] = React.useState(null);
  const [why, setWhy] = React.useState(null);   // { id, focus } — the row whose drawer is open
  const prevStatus = React.useRef(null);
  // #/prospecting?batch=<id> opens that batch (the Signals page links here
  // after "Prospect"); otherwise the newest one.
  const batchFromHash = () => {
    try { return new URLSearchParams((window.location.hash.split('?')[1] || '')).get('batch') || null; } catch (_) { return null; }
  };
  const loadBatches = React.useCallback(() => window.AtlasApi.prospectingBatches().then((r) => {
    setBatches(r.items || []);
    setBatchId((cur) => cur || batchFromHash() || ((r.items || [])[0] || {}).id || null);
  }).catch(() => setBatches([])), []);
  const loadBatch = React.useCallback(() => {
    if (!batchId) { setBatch(null); setItems([]); setFunnel(null); return; }
    Promise.all([window.AtlasApi.prospectingBatch(batchId), window.AtlasApi.prospectingProspects(batchId, stage ? { stage } : {})])
      .then(([b, r]) => { setBatch(b); setItems(r.items || []); setFunnel(r.funnel || b.funnel); setLoadError(null); })
      .catch((e) => { setItems([]); setLoadError(e && e.message ? e.message : 'Could not load the batch'); });
  }, [batchId, stage]);
  // A new batch shows "Loading…", never the previous batch's rows under the new name.
  React.useEffect(() => { setItems(null); setLoadError(null); setWhy(null); }, [batchId]);
  React.useEffect(() => { loadBatches(); window.AtlasApi.listIndustries().then((r) => setIndustries((r || []).filter((i) => i.active !== false))).catch(() => {}); }, [loadBatches]);
  React.useEffect(() => { loadBatch(); setSelected(new Set()); }, [loadBatch]);
  React.useEffect(() => {
    // When a run ends, the sidebar counts and a toast catch up with it.
    const status = batch && batch.status;
    if (prevStatus.current === 'running' && status && status !== 'running') {
      loadBatches();
      window.pushToast?.({ type: status === 'failed' ? 'error' : 'success', title: status === 'failed' ? 'Run failed' : 'Run finished', body: status === 'failed' ? (batch.run_error || '') : `${batch.name}` });
    }
    prevStatus.current = status;
    if (!batch || status !== 'running') return undefined;
    const t = setInterval(loadBatch, 3000);
    return () => clearInterval(t);
  }, [batch && batch.status, loadBatch, loadBatches]);
  const onRowChange = (updated) => {
    if (!updated || !updated.id) { loadBatch(); return; }
    setItems((prev) => (prev || []).map((x) => (x.id === updated.id ? updated : x)));
    window.AtlasApi.prospectingBatch(batchId).then((b) => { setBatch(b); setFunnel(b.funnel); }).catch(() => {});
    if (updated.stage === 'sent') loadBatches();   // the sidebar's sent count
  };
  // One row again (a website that did not load) — the run skips the rest.
  const retry = async (id) => {
    try {
      await window.AtlasApi.prospectingRun(batchId, { steps: ['identify', 'ownership', 'qualify'], ids: [id] });
      loadBatch(); setTimeout(loadBatch, 1500);
    } catch (e) { window.pushToast?.({ type: 'error', title: 'Could not retry', body: e.message }); }
  };
  // The revenue step alone, again, on one row (the drawer's button).
  const estimate = async (id) => {
    try {
      await window.AtlasApi.prospectingRun(batchId, { steps: ['revenue'], ids: [id], force: true });
      loadBatch(); setTimeout(loadBatch, 1500);
    } catch (e) { window.pushToast?.({ type: 'error', title: 'Could not estimate', body: e.message }); }
  };
  const sendAll = async () => {
    const ok = await window.showConfirm({ title: `Send ${F.approved} approved?`, body: 'Creates the companies and contacts in Atlas and hands the owners to the Email Copilot. PhoneBurner gets them after the last email if they haven’t replied; owners it can’t email go to the call queue now.', confirmLabel: 'Send' });
    if (!ok) return;
    try {
      const r = await window.AtlasApi.prospectingSendApproved(batchId);
      const got = [r.emailing ? `${r.emailing} to email` : null, r.calls ? `${r.calls} to call` : null].filter(Boolean).join(' · ');
      window.pushToast?.({ type: got ? 'success' : 'warning', title: `Sent ${r.sent} — ${got || 'nobody to reach'}`,
        body: [r.nobody && got ? `${r.nobody} with nobody to reach` : null, r.failed.length ? `${r.failed.length} failed` : null].filter(Boolean).join(' · ') || undefined });
      loadBatch(); loadBatches();
    } catch (e) { window.pushToast?.({ type: 'error', title: 'Could not send', body: e.message }); }
  };
  const decideSelected = async (decision) => {
    const ids = [...selected];
    if (!ids.length) return;
    try {
      const r = await window.AtlasApi.prospectingDecideMany(batchId, ids, decision);
      window.pushToast?.({ type: 'success', title: `${r.changed} ${decision === 'approved' ? 'approved' : decision === 'rejected' ? 'rejected' : 'reopened'}` });
      setSelected(new Set()); loadBatch(); loadBatches();
    } catch (e) { window.pushToast?.({ type: 'error', title: 'Could not update', body: e.message }); }
  };
  const rename = async () => {
    if (!newName.trim() || newName.trim() === batch.name) { setRenaming(false); return; }
    try { const b = await window.AtlasApi.prospectingRenameBatch(batchId, { name: newName.trim() }); setBatch(b); setRenaming(false); loadBatches(); }
    catch (e) { window.pushToast?.({ type: 'error', title: 'Could not rename', body: e.message }); }
  };
  const remove = async () => {
    const F0 = funnel || {};
    const ok = await window.showConfirm({ title: `Delete “${batch.name}”?`, body: `${F0.total || 0} lead${F0.total === 1 ? '' : 's'} in this batch go away.${F0.sent ? ` The ${F0.sent} already sent stay in Atlas.` : ''}`, confirmLabel: 'Delete', danger: true });
    if (!ok) return;
    try {
      await window.AtlasApi.prospectingDeleteBatch(batchId);
      window.pushToast?.({ type: 'success', title: 'Batch deleted' });
      setBatchId(null); setBatch(null); setItems([]); setFunnel(null);
      const r = await window.AtlasApi.prospectingBatches(); setBatches(r.items || []); setBatchId(((r.items || [])[0] || {}).id || null);
    } catch (e) { window.pushToast?.({ type: 'error', title: 'Could not delete', body: e.message }); }
  };
  const F = funnel || {};
  const needle = search.trim().toLowerCase();
  const visible = (items || []).filter((p) => (!tier || p.size_tier === tier) && (!needle || `${(p.identity || {}).company_name || ''} ${p.input_name || ''} ${p.domain || ''}`.toLowerCase().includes(needle)));
  const selectable = visible.filter((p) => p.stage !== 'sent' && p.stage !== 'rejected');
  const allSelected = selectable.length > 0 && selectable.every((p) => selected.has(p.id));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((p) => p.id)));
  const openWhy = (id, focus) => setWhy({ id, focus: focus || null });
  const whyRow = why ? (items || []).find((x) => x.id === why.id) || null : null;
  const whyAt = whyRow ? visible.findIndex((x) => x.id === whyRow.id) : -1;
  const running = !!batch && batch.status === 'running';
  const funnelTab = (k, label, n, hue) => (
    <button key={k || 'all'} onClick={() => setStage(k)} style={{
      padding: '6px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: stage === k ? 600 : 500,
      color: stage === k ? 'var(--text)' : 'var(--text-3)', borderBottom: stage === k ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1,
      display: 'inline-flex', alignItems: 'center', gap: 6,
    }}>{label}<span style={{ fontSize: 10.5, color: hue ? `var(--${hue}-fg, var(--text-4))` : 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{n != null ? n : '—'}</span></button>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', height: '100%' }}>
      <div style={{ borderRight: '1px solid var(--border)', overflow: 'auto', padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 13.5, fontWeight: 650 }}>Batches</span>
          <span style={{ flex: 1 }} />
          <window.Btn size="xs" variant="primary" onClick={() => setCreating(true)}>New</window.Btn>
        </div>
        {batches === null && <div style={{ fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>}
        {batches && batches.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.5 }}>No batches yet.</div>}
        {(batches || []).map((b) => (
          <button key={b.id} onClick={() => { setBatchId(b.id); setStage(''); setTier(''); setSearch(''); setRenaming(false); }} style={{
            display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
            border: `1px solid ${b.id === batchId ? 'var(--accent)' : 'var(--border)'}`, background: b.id === batchId ? 'var(--accent-soft)' : 'var(--surface)', color: 'inherit',
          }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</div>
            <div style={{ fontSize: 10.5, color: 'var(--text-4)', marginTop: 2 }}>
              {SOURCE_SHORT[b.source] ? `${SOURCE_SHORT[b.source]} · ` : ''}{b.total} leads · {b.ready} ready · {b.sent} sent{b.rejected ? ` · ${b.rejected} out` : ''}{b.status === 'running' ? ' · running' : ''}
            </div>
          </button>
        ))}
      </div>
      <div style={{ overflow: 'auto', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {renaming && batch ? (
            <>
              <window.TextInput value={newName} autoFocus onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setRenaming(false); }} style={{ width: 360, fontSize: 15, fontWeight: 600 }} />
              <window.Btn size="xs" variant="primary" onClick={rename}>Save</window.Btn>
              <window.Btn size="xs" variant="ghost" onClick={() => setRenaming(false)}>Cancel</window.Btn>
            </>
          ) : (
            <h1 style={{ fontSize: 18, fontWeight: 650, margin: 0 }}>Prospecting{batch ? ` · ${batch.name}` : ''}</h1>
          )}
          {batch && !renaming && (
            <>
              <window.Btn size="xs" variant="ghost" title="Rename" onClick={() => { setNewName(batch.name); setRenaming(true); }}>Rename</window.Btn>
              <window.Btn size="xs" variant="ghost" title="Delete this batch" disabled={batch.status === 'running'} onClick={remove}>Delete</window.Btn>
            </>
          )}
          <span style={{ flex: 1 }} />
          {batch && batch.query && <QueryPills q={batch.query} />}
        </div>
        {batch && <RunPanel batch={batch} onStarted={loadBatch} />}
        {batch && (
          <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', alignItems: 'center', flexWrap: 'wrap' }}>
            {funnelTab('', 'All', F.total)}
            {funnelTab('intake', STAGE_LABEL.intake, F.intake)}
            {funnelTab('identified', 'Identified', F.identified)}
            {funnelTab('qualified', 'Qualified', F.qualified, 'blue')}
            {funnelTab('enriched', 'Enriched', F.enriched, 'teal')}
            {funnelTab('sent', 'Sent', F.sent, 'green')}
            {funnelTab('rejected', 'Rejected', F.rejected, 'rose')}
            <span style={{ flex: 1 }} />
            <window.Picker value={tier} onChange={setTier} size="sm" title="Size tier" placeholder="Size tier" emptyLabel="All tiers" style={{ marginBottom: 4 }}
              options={Object.entries(TIER_LABEL).map(([k, l]) => ({ value: k, label: l }))} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find in batch" style={{ ...selectStyle, width: 150, marginBottom: 4 }} />
            {F.approved > 0 && <window.Btn size="xs" variant="primary" onClick={sendAll} style={{ marginBottom: 4 }}>Send {F.approved} approved</window.Btn>}
          </div>
        )}
        {batch && selected.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', border: '1px solid var(--accent)', borderRadius: 8, background: 'var(--accent-soft)', fontSize: 12.5 }}>
            <span style={{ fontWeight: 600 }}>{selected.size} selected</span>
            <window.Btn size="xs" variant="primary" onClick={() => decideSelected('approved')}>Approve</window.Btn>
            <window.Btn size="xs" variant="outline" onClick={() => decideSelected('rejected')}>Reject</window.Btn>
            <span style={{ flex: 1 }} />
            <window.Btn size="xs" variant="ghost" onClick={() => setSelected(new Set())}>Clear</window.Btn>
          </div>
        )}
        {batch && (
          <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflowX: 'auto', minWidth: 0 }}>
            <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 8, padding: '6px 12px', fontSize: 10.5, color: 'var(--text-4)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, alignItems: 'center' }}>
              <span><input type="checkbox" checked={allSelected} disabled={!selectable.length} onChange={toggleAll} style={{ margin: 0 }} title="Select all open rows shown" /></span>
              <span>Company</span><span>Atlas</span><span>Ownership</span><span>Industry</span><span>Geography</span><span>Size tier</span><span>Revenue</span><span>Owners</span><span style={{ textAlign: 'right' }}>Decision</span>
            </div>
            {items === null && <div style={{ padding: 14, fontSize: 12, color: 'var(--text-4)' }}>Loading…</div>}
            {items && loadError && <div style={{ padding: 18, fontSize: 12.5, color: 'var(--rose-fg, #c33)' }}>{loadError}</div>}
            {items && !loadError && visible.length === 0 && <div style={{ padding: 18, fontSize: 12.5, color: 'var(--text-3)' }}>Nothing here.</div>}
            {visible.map((p) => <ProspectRow key={p.id} p={p} industries={industries} goToCompany={goToCompany} onChange={onRowChange} selected={selected.has(p.id)} onToggle={toggle}
              onWhy={openWhy} onRetry={retry} batchSource={batch.source} running={running} />)}
          </div>
        )}
        {!batch && batches && batches.length === 0 && (
          <div style={{ padding: 24, border: '1px dashed var(--border)', borderRadius: 10, fontSize: 13, color: 'var(--text-3)', lineHeight: 1.6, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>Find leads from Apollo, Google Maps or the Atlas lead pool — or paste a list.</span>
            <window.Btn size="sm" variant="primary" onClick={() => setCreating(true)}>New batch</window.Btn>
          </div>
        )}
      </div>
      <NewBatchDrawer open={creating} onClose={() => setCreating(false)} onCreated={(b) => { setCreating(false); loadBatches(); setBatchId(b.id); setStage(''); setTier(''); setSearch(''); }} />
      <ProspectWhy p={whyRow} batch={batch} focus={why && why.focus} goToCompany={goToCompany} running={running}
        onClose={() => setWhy(null)} onChange={onRowChange} onRetry={retry} onEstimate={estimate}
        onPrev={whyAt > 0 ? () => setWhy({ id: visible[whyAt - 1].id, focus: null }) : null}
        onNext={whyAt >= 0 && whyAt < visible.length - 1 ? () => setWhy({ id: visible[whyAt + 1].id, focus: null }) : null} />
    </div>
  );
};

Object.assign(window, { ProspectingView });
